<script setup>
import { watch, inject, computed, onMounted, ref, onBeforeMount } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import AppButton from '#/general/AppButton'
import { useAnalyzer } from '#/analyzer/useAnalyzer'
import { useToast } from 'vue-toast-notification'
import { useLoader } from '#/general/useLoader'
import AppInput from '#/general/AppInput'
import AppMenu from '#/general/AppMenu'
import TooltipContainer from '#/general/TooltipContainer'
import { useGA } from '#/useGA'
import debounce from 'debounce'
const $toast = useToast({ position: 'bottom' })
const { components, global, screen, application, analytics } = inject('Shared')
const { showLoader, hideLoader } = useLoader()
const {
  searchText,
  selectedSearchResult,
  showPopup,
  searchResults,
  findPotentialEnabled,
  searchAddressesEnabled,
  invalidPotentialBuildingCategory,
  invalidPotentialBuildingClass,
  showInvalidPotentialPopup,
  selectedPotential,
  showQuickView,
  sharedApolloClient
} = useAnalyzer()
const props = defineProps(['page'])
const emit = defineEmits(['PotentialFound'])
const { widgetEvent } = useGA()

let searchAddresses = async () => {
  try {
    if (searchText.value && searchAddressesEnabled.value) {
      const res = await sharedApolloClient.value.query({
        query: gql`
        query searchPotentialAddress ($searchText: String!) {
          searchPotentialAddress (searchText: $searchText) {
            address,
            id
          }
        }
      `,
        variables: { searchText: searchText.value }
      })
      showPopup.value = true
      if (res.data) {
        searchResults.value = JSON.parse(JSON.stringify(res?.data?.searchPotentialAddress))
      }
    }
  } catch (e) {
    console.log('Search error: ', JSON.parse(JSON.stringify(e)))
  }
}
searchAddresses = debounce(searchAddresses, 100)
watch(searchText, searchAddresses, { immediate: true })

const { onError: onFoundPotentialError, onResult: onFoundPotential, loading: findPotentialLoading } = useQuery(
  gql`
    query findPotentialByAddress ($addressId: String!, $address: String!) {
      findPotentialByAddress (addressId: $addressId, address: $address) {
        searchedAddress,
        queryId,
        success,
        error,
        potential {
          _id,
          lat,
          long,
          hasLead,
          yearOfConstructionEndMax,
          city {
            name
            buildingRegulationsPage
          }
          parcel {
            area,
            parcelNr
            builtUpArea
            maxVolume
            currentVolume
            publicTransportQuality
            renovationPressure
            roofStats {
              flatRoofShare
            }
            geometryCoordinates {
              lat
              lon
            }
          }
          buildingZones {
            squareMeters
            buildingZone {
              shortName
              zoneType
            }
          }
          buildings {
            address,
            town,
            buildingCategory,
          }
          analysis {
            score
            potentialVolume
            isBuildingZoneBased
          }
          oerebLink
        }
      }
    }
  `,
  () => { return { addressId: selectedSearchResult.value.addressId, address: selectedSearchResult.value.address } },
  () => { return { enabled: !!(selectedSearchResult.value.address && findPotentialEnabled.value === true), metadata: false, debounce: 300 } }
)
onFoundPotentialError(findPotentialError => {
  console.log('FIND POTENTIAL ERROR', JSON.parse(JSON.stringify(findPotentialError)))
  $toast.open({ type: 'info', message: 'Something went wrong while analyzing this address, please contact us at hallo@raumpioniere.ch to sort this out quickly.', duration: 5000 })
  if (findPotentialError.message) {
    const serverMessage = JSON.parse(findPotentialError.message)
    if (serverMessage.reason === 'INVALID_SEP_POTENTIAL') {
      showInvalidPotentialPopup.value = true
      invalidPotentialBuildingClass.value = serverMessage.buildingClass
      invalidPotentialBuildingCategory.value = serverMessage.buildingCategory
    }
  }
})
onFoundPotential(res => {
  try {
    res = JSON.parse(JSON.stringify(res))
    showPopup.value = false
    if (res.data) {
      if (res.data && res.data.findPotentialByAddress) {
        if (res.data.findPotentialByAddress.success) {
          selectedPotential.value = { ...res.data.findPotentialByAddress.potential, searchedAddress: res.data.findPotentialByAddress.searchedAddress, queryId: res.data.findPotentialByAddress.queryId }
          emit('PotentialFound')
          if (application === 'WIDGET') {
            widgetEvent(analytics.value, 'searchInitiated')
          }
        } else {
          console.log('E', res.data)
          const error = JSON.parse(res.data.findPotentialByAddress.error)
          console.log('ERROR', error)
          if (error.reason === 'INVALID_SEP_POTENTIAL') {
            showInvalidPotentialPopup.value = true
            invalidPotentialBuildingClass.value = error.buildingClass
            invalidPotentialBuildingCategory.value = error.buildingCategory
          }
        }
      }
    }
  } catch (e) {
    console.log(e)
    console.log(JSON.parse(JSON.stringify(e)))
    searchText.value = null
    $toast.open({ type: 'info', message: 'Something went wrong while analyzing this address, please contact us at hallo@raumpioniere.ch to sort this out quickly.', duration: 5000 })
  }
})

const timer = ref()
const timer2 = ref()
watch(findPotentialLoading, v => {
  if (v) {
    showLoader({
      message: 'Sämtliche Daten für die Potenzialanalyse werden gesammelt.',
      type: 'IOS'
    })
    timer.value = setTimeout(() => {
      if (findPotentialLoading.value) {
        showLoader({
          message: 'Die Berechnungen laufen ...',
          type: 'HOURGLASS'
        })
        timer2.value = setTimeout(() => {
          if (findPotentialLoading.value) {
            showLoader({
              message: '... gleich sind wir soweit. Etwas Geduld.',
              type: 'HOURGLASS'
            })
          }
        }, 4000)
      }
    }, 4000)
  } else {
    hideLoader()
    clearTimeout(timer.value)
    clearTimeout(timer2.value)
    timer.value = void 0
    timer2.value = void 0
  }
}, { immediate: true })

onBeforeMount(() => {
  clearTimeout(timer.value)
  clearTimeout(timer2.value)
  timer.value = void 0
  timer2.value = void 0
})

watch(searchText, value => {
  if (!value) {
    showPopup.value = false
    searchResults.value = []
  } else {
    searchAddressesEnabled.value = true
    const sameRes = searchResults.value.find(e => e.address === value)
    if (sameRes) {
      resultClicked(sameRes)
    } else {
      selectedSearchResult.value = { address: null, addressId: null }
    }
  }
})

function resultClicked (result) {
  showPopup.value = false
  searchAddressesEnabled.value = false
  findPotentialEnabled.value = false
  selectedSearchResult.value = { addressId: result.id, address: result.address }
  searchText.value = result.address
}

function openPotential () {
  if (selectedSearchResult.value) {
    findPotentialEnabled.value = true
    if (application === 'WIDGET' && selectedPotential.value && selectedPotential.value.buildings.some(building => building.address.toLowerCase() === searchText.value.toLowerCase())) {
      showQuickView.value = true
    }
  }
}

const buttonLabel = computed(() => {
  if (screen.gt.sm) {
    return components.value.searchBox.button.text
  } else {
    if (['B2BAnalyzer', 'B2CAnalyzer', 'WidgetAnalyzer'].includes(props.page)) {
      return undefined
    }
    return components.value.searchBox.button.textMobile
  }
})

const showSeparator = computed(() => {
  if (screen.gt.sm) {
    if (['Landing', 'B2C', 'WidgetAnalyzer'].includes(props.page)) {
      return true
    }
  } else {
    if (['Landing', 'B2C'].includes(props.page)) {
      return true
    }
  }
  return false
})

const inputFormHeight = computed(() => {
  if (screen.gt.sm) {
    if (props.page === 'B2C') {
      return '97px'
    } else if (props.page === 'Landing') {
      return '72px'
    }
    return '60px'
  } else {
    if (props.page === 'B2C') {
      return '87px'
    } else if (props.page === 'Landing') {
      return '75px'
    }
    return '60px'
  }
})
onMounted(() => {
  window.addEventListener('keydown', (e) => {
    if (e.keyCode === 32 && e.target.parentElement.id === 'map') {
      e.stopImmediatePropagation()
    }
  })
})
</script>

<template lang='pug'>
.analyzerSearchBoxContainer(:style="['B2BAnalyzer', 'B2CAnalyzer', 'WidgetAnalyzer'].includes(page) ? 'width: 100%' : ''")
  .analyzerSearchForm(:class="`${['B2C'].includes(page) ? 'inputPadding' : ''}`")
    .analyzerSearchInputContainer
      app-menu(v-if="!findPotentialLoading" v-model:visible='showPopup' position="bottom" attach-to-parent persistent)
        template(#parent)
          app-input(
            :background="screen.gt.xs ? components.searchBox.input.background : 'transparent'"
            :hide-separator="['B2BAnalyzer', 'B2CAnalyzer'].includes(page)"
            height="45px"
            v-model:value="searchText"
            @clicked='showPopup = true'
            :show-separator="showSeparator"
            :color="components.searchBox.input.color"
            :separator-color="page === 'B2C' ? '#B4BCCE' : global.graphicElementColor"
            :size="screen.gt.sm ? components.searchBox.input.fontSize : components.searchBox.input.fontSizeMobile"
            :placeholder="screen.gt.sm ? components.searchBox.input.text : components.searchBox.input.textMobile"
          )
        template(#content)
          .resultContainer(v-if='searchResults && searchResults.length > 0')
            template(v-for='(result, index) in searchResults.slice(0, 3)' :key="index")
              .result(@click="resultClicked(result)" v-if="result && result.address") {{ result.address }}
          .noResultText(v-else-if="searchText && (!searchResults || searchResults.length === 0) && showInvalidPotentialPopup === false") Diese Adresse konnte leider nicht gefunden werden. Bitte suchen Sie nach einer anderen Adresse.
    app-menu(position="bottom" content-width="350px" tooltip-mode attach-to-parent)
      template(#parent)
        app-button.submitBtn(
          v-if="screen.gt.sm || ['B2BAnalyzer', 'B2CAnalyzer', 'WidgetAnalyzer'].includes(page)"
          :label="buttonLabel"
          :disabled="!selectedSearchResult || !selectedSearchResult.address || !searchText"
          @clicked="openPotential"
          :bg-color="global.buttonBackground"
          :bg-hover="global.buttonBackgroundHover"
          :label-color="global.buttonColor"
        )
          template(#default)
            .submitBtnIcon(v-if="['B2BAnalyzer', 'B2CAnalyzer', 'WidgetAnalyzer'].includes(page)")
              svg(width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg')
                path(d='M9.18981 1.57453C7.09044 -0.524843 3.67356 -0.524843 1.57419 1.57453C-0.524729 3.67435 -0.524729 7.09078 1.57419 9.19061C3.44373 11.0597 6.35546 11.2599 8.45346 9.80044C8.49761 10.0093 8.59864 10.2087 8.76111 10.3711L11.8185 13.4285C12.264 13.8731 12.984 13.8731 13.4272 13.4285C13.8723 12.9834 13.8723 12.2634 13.4272 11.8197L10.3699 8.76145C10.2083 8.60035 10.0085 8.49886 9.79965 8.45471C11.2601 6.35625 11.0598 3.44498 9.18981 1.57453ZM8.22455 8.22534C6.65719 9.7927 4.10636 9.7927 2.53945 8.22534C0.973001 6.65798 0.973001 4.10761 2.53945 2.54025C4.10636 0.973343 6.65719 0.973343 8.22455 2.54025C9.79191 4.10761 9.79191 6.65798 8.22455 8.22534Z' fill='white')
      template(#content v-if="(!selectedSearchResult || !selectedSearchResult.address || !searchText) && !showQuickView")
        tooltip-container(body="Bitte suchen sie nach einer Adresse und wählen diese aus der Liste aus.")

  .lt-md(v-if="['B2C', 'Landing'].includes(page)")
    app-menu(position="bottom" content-width="350px" tooltip-mode attach-to-parent)
      template(#parent)
        app-button.mobileSearchBtn(
          :bg-color="global.buttonBackground"
          :bg-hover="global.buttonBackgroundHover"
          :label="components.searchBox.button.textMobile"
          @clicked="openPotential"
          :disabled="!selectedSearchResult || !selectedSearchResult.address || !searchText"
        )
      template(#content)
        tooltip-container(
          v-if="!selectedSearchResult || !selectedSearchResult.address || !searchText"
          body="Bitte suchen sie nach einer Adresse und wählen diese aus der Liste aus."
        )
  slot
</template>

<style lang='sass'>
textarea:focus, input:focus
  outline: none
*:focus
  outline: none
.noResultText
  padding: 5px 10px 5px 20px
  font-size: 13px
  color: black
  background: white
.resultContainer
  background: v-bind('components.searchBox.resultsBackgroundColor')
  color: v-bind('components.searchBox.resultsColor')
  .result
    font-size: 14px
    transition: all 0.3s ease
    cursor: pointer
    padding: 5px 10px
    &:hover
      background: #626C851A
  .resultSeparator
    background: #B4BCCE
    height: 1px
    margin: 5px 0
    width: 100%
.analyzerSearchBoxContainer
  display: flex
  flex-direction: column
  @media screen and (min-width: 1024px)
    width: 100%
    max-width: 700px
  @media screen and (max-width: 1023px)
    width: 100%
  .analyzerSearchForm
    background: white !important
    border: 1px solid rgba(98, 108, 133, 0.1)
    box-shadow: 0px 15px 30px rgba(166, 166, 166, 0.25)
    border-radius: 10px
    box-sizing: border-box
    display: flex
    flex-direction: row
    justify-content: space-between
    align-items: center
    position: relative
    height: v-bind(inputFormHeight)
    max-width: 100%
    @media screen and (max-width: 799px)
      width: 100%
    .analyzerSearchInputContainer
      border-radius: 10px
      flex: 1
      @media screen and (min-width: 1024px)
        padding: 0 30px
      @media screen and (max-width: 1023px)
        padding: 0 15px !important
      input::placeholder
        color: v-bind('components.searchBox.input.color') !important
  .inputPadding
    @media screen and (min-width: 1024px)
      padding: 15px 0
    @media screen and (max-width: 1023px)
      padding: 7px 0
  .mobileSearchBtn
    max-width: 100%
    @media screen and (min-width: 1024px)
      margin-top: 10px
    @media screen and (max-width: 1023px)
      margin: 10px 0 60px 0
  .submitBtn
    margin-right: 10px
    @media screen and (min-width: 1024px)
      width: 259px
    @media screen and (max-width: 1023px)
      width: 50px
    .label
      font-size: 18px !important
  .submitBtnIcon
    @media screen and (min-width: 1024px)
      margin-left: 10px
    @media screen and (max-width: 1023px)
      margin-top: 3px
    svg
      margin-top: 4px
      @media screen and (min-width: 1024px)
        width: 13px
        height: 13px
      @media screen and (max-width: 1023px)
        width: 17px
        height: 17px
</style>
